import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getPublishedPosts, getPostPublishDate, formatPublishDate } from '@/utils/scheduler';
import { categories } from '@/mocks/categories';
import { Post } from '@/mocks/posts';
import { useSeoMeta } from '@/hooks/useSeoMeta';

const BASE_URL = import.meta.env.VITE_SITE_URL ?? 'https://roameurope.blog';

type SortOption = 'newest' | 'oldest' | 'shortest' | 'longest';
type ViewMode = 'list' | 'grid';

export default function ArchivePage() {
  const allPosts = useMemo(() => getPublishedPosts(), []);

  useSeoMeta({
    title: 'All Articles — Complete Relationship Advice Archive',
    description: `Browse all ${allPosts.length} published relationship articles on roameurope.blog. Filter by topic, sort by date or read time.`,
    url: '/archive',
    type: 'website',
    tags: ['relationship advice', 'dating tips', 'all articles', 'archive'],
  });

  const archiveSchema = useMemo(() => JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'All Articles — roameurope.blog',
    description: `Browse all ${allPosts.length} published relationship articles on roameurope.blog.`,
    url: `${BASE_URL}/archive`,
    publisher: {
      '@type': 'Organization',
      name: 'roameurope.blog',
      url: BASE_URL,
    },
    hasPart: allPosts.map((post) => ({
      '@type': 'Article',
      headline: post.title,
      url: `${BASE_URL}/post/${post.slug}`,
      image: post.image,
      author: { '@type': 'Person', name: post.author },
    })),
  }), [allPosts]);

  const [activeCategory, setActiveCategory] = useState('all');
  const [sort, setSort] = useState<SortOption>('newest');
  const [view, setView] = useState<ViewMode>('list');
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    let result = [...allPosts];

    if (activeCategory !== 'all') {
      result = result.filter((p) => p.category === activeCategory);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.tags.some((t) => t.toLowerCase().includes(q)) ||
          p.author.toLowerCase().includes(q)
      );
    }

    switch (sort) {
      case 'oldest':
        result.sort((a, b) => a.publishIndex - b.publishIndex);
        break;
      case 'shortest':
        result.sort((a, b) => a.readTime - b.readTime);
        break;
      case 'longest':
        result.sort((a, b) => b.readTime - a.readTime);
        break;
      default:
        result.sort((a, b) => b.publishIndex - a.publishIndex);
    }

    return result;
  }, [allPosts, activeCategory, sort, search]);

  // Group by month for list view
  const grouped = useMemo(() => {
    if (view !== 'list') return null;
    const map = new Map<string, Post[]>();
    filtered.forEach((post) => {
      const d = getPostPublishDate(post);
      const key = d.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(post);
    });
    return map;
  }, [filtered, view]);

  const totalReadTime = useMemo(
    () => filtered.reduce((acc, p) => acc + p.readTime, 0),
    [filtered]
  );

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: archiveSchema }}
      />
      <Navbar />

      {/* Page header */}
      <section className="pt-28 pb-10 bg-white border-b border-stone-100">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <h1
                className="text-4xl md:text-5xl font-bold text-stone-900 leading-tight"
                style={{ fontFamily: "'Teko', sans-serif", letterSpacing: '0.02em' }}
              >
                All Articles
              </h1>
              <p className="text-stone-400 text-sm mt-1">
                {allPosts.length} articles published &middot; {Math.round(totalReadTime / 60)}h+ of reading
              </p>
            </div>

            {/* Search */}
            <div className="relative w-full md:w-72">
              <div className="w-5 h-5 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-stone-400">
                <i className="ri-search-line text-sm" />
              </div>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter by title, tag, author..."
                className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-stone-200 bg-stone-50 text-sm text-stone-700 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-rose-200 transition-all"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-stone-400 hover:text-stone-600 cursor-pointer"
                >
                  <i className="ri-close-line text-sm" />
                </button>
              )}
            </div>
          </div>

          {/* Stats row */}
          <div className="flex flex-wrap gap-4 mt-6">
            {categories.map((cat) => {
              const count = allPosts.filter((p) => p.category === cat.slug).length;
              return (
                <div key={cat.slug} className="flex items-center gap-1.5">
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: cat.color }}
                  />
                  <span className="text-xs text-stone-500">
                    {cat.name} <span className="font-semibold text-stone-700">({count})</span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Filters bar */}
      <div className="bg-white border-b border-stone-100 sticky top-16 z-30">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
          {/* Category tabs */}
          <div className="flex items-center gap-2 flex-wrap flex-1">
            <button
              onClick={() => setActiveCategory('all')}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === 'all'
                  ? 'bg-stone-800 text-white'
                  : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
              }`}
            >
              All Topics
            </button>
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => setActiveCategory(cat.slug)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeCategory === cat.slug
                    ? 'text-white'
                    : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                }`}
                style={activeCategory === cat.slug ? { backgroundColor: cat.color } : {}}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {/* Sort + view toggle */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as SortOption)}
              className="text-xs border border-stone-200 rounded-lg px-2.5 py-1.5 text-stone-600 bg-white focus:outline-none focus:ring-2 focus:ring-rose-200 cursor-pointer"
            >
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="shortest">Shortest read</option>
              <option value="longest">Longest read</option>
            </select>

            <div className="flex items-center border border-stone-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setView('list')}
                className={`w-8 h-8 flex items-center justify-center transition-colors cursor-pointer ${
                  view === 'list' ? 'bg-stone-800 text-white' : 'text-stone-400 hover:bg-stone-50'
                }`}
                aria-label="List view"
              >
                <i className="ri-list-check text-sm" />
              </button>
              <button
                onClick={() => setView('grid')}
                className={`w-8 h-8 flex items-center justify-center transition-colors cursor-pointer ${
                  view === 'grid' ? 'bg-stone-800 text-white' : 'text-stone-400 hover:bg-stone-50'
                }`}
                aria-label="Grid view"
              >
                <i className="ri-grid-line text-sm" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 md:px-8 py-10">
        {/* Result count */}
        <p className="text-xs text-stone-400 mb-6">
          Showing <span className="font-semibold text-stone-700">{filtered.length}</span> of{' '}
          <span className="font-semibold text-stone-700">{allPosts.length}</span> articles
          {activeCategory !== 'all' && (
            <> in <span className="text-rose-500 font-medium">{categories.find((c) => c.slug === activeCategory)?.name}</span></>
          )}
          {search && (
            <> matching &ldquo;<span className="text-rose-500 font-medium">{search}</span>&rdquo;</>
          )}
        </p>

        {/* Empty state */}
        {filtered.length === 0 && (
          <div className="text-center py-20">
            <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 text-stone-300">
              <i className="ri-article-line text-5xl" />
            </div>
            <p className="text-stone-500 font-medium mb-2">No articles found</p>
            <p className="text-stone-400 text-sm mb-5">Try a different category or clear your search</p>
            <button
              onClick={() => { setActiveCategory('all'); setSearch(''); }}
              className="px-5 py-2 bg-rose-500 text-white rounded-lg text-sm font-medium hover:bg-rose-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Show all articles
            </button>
          </div>
        )}

        {/* Grid view */}
        {view === 'grid' && filtered.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((post) => (
              <GridCard key={post.id} post={post} />
            ))}
          </div>
        )}

        {/* List view — grouped by month */}
        {view === 'list' && grouped && filtered.length > 0 && (
          <div className="space-y-10">
            {Array.from(grouped.entries()).map(([month, monthPosts]) => (
              <div key={month}>
                <div className="flex items-center gap-3 mb-4">
                  <h2 className="text-xs font-bold text-stone-400 uppercase tracking-widest whitespace-nowrap">
                    {month}
                  </h2>
                  <div className="flex-1 h-px bg-stone-200" />
                  <span className="text-xs text-stone-400 whitespace-nowrap">{monthPosts.length} article{monthPosts.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="space-y-2">
                  {monthPosts.map((post) => (
                    <ListRow key={post.id} post={post} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}

/* ── List row ── */
function ListRow({ post }: { post: Post }) {
  const cat = categories.find((c) => c.slug === post.category);
  const publishDate = getPostPublishDate(post);

  return (
    <Link
      to={`/post/${post.slug}`}
      className="group flex items-center gap-4 bg-white rounded-xl border border-stone-100 px-5 py-4 hover:border-rose-200 transition-all cursor-pointer"
    >
      {/* Thumbnail */}
      <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden hidden sm:block">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
        />
      </div>

      {/* Main content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          {cat && (
            <span
              className="text-xs font-semibold uppercase tracking-wider whitespace-nowrap"
              style={{ color: cat.color }}
            >
              {cat.name}
            </span>
          )}
          <span className="text-stone-300 text-xs hidden sm:inline">&middot;</span>
          <span className="text-xs text-stone-400 whitespace-nowrap hidden sm:inline">
            {formatPublishDate(publishDate)}
          </span>
        </div>
        <h3 className="text-sm font-semibold text-stone-800 leading-snug group-hover:text-rose-600 transition-colors line-clamp-1">
          {post.title}
        </h3>
        <p className="text-xs text-stone-400 mt-0.5 line-clamp-1 hidden md:block">
          {post.excerpt}
        </p>
      </div>

      {/* Meta right */}
      <div className="flex-shrink-0 flex flex-col items-end gap-1 text-xs text-stone-400">
        <span className="flex items-center gap-1 whitespace-nowrap">
          <i className="ri-time-line" />
          {post.readTime} min
        </span>
        <span className="flex items-center gap-1 whitespace-nowrap hidden sm:flex">
          <i className="ri-user-line" />
          {post.author.split(' ')[0]}
        </span>
      </div>

      {/* Arrow */}
      <div className="w-5 h-5 flex items-center justify-center text-stone-300 group-hover:text-rose-400 transition-colors flex-shrink-0">
        <i className="ri-arrow-right-line text-sm" />
      </div>
    </Link>
  );
}

/* ── Grid card ── */
function GridCard({ post }: { post: Post }) {
  const cat = categories.find((c) => c.slug === post.category);
  const publishDate = getPostPublishDate(post);

  return (
    <Link
      to={`/post/${post.slug}`}
      className="group bg-white rounded-xl border border-stone-100 overflow-hidden hover:border-rose-200 transition-all cursor-pointer block"
    >
      <div className="w-full h-44 overflow-hidden">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          {cat && (
            <span
              className="text-xs font-semibold uppercase tracking-wider whitespace-nowrap"
              style={{ color: cat.color }}
            >
              {cat.name}
            </span>
          )}
          <span className="text-xs text-stone-400 whitespace-nowrap">{post.readTime} min</span>
        </div>
        <h3 className="text-sm font-semibold text-stone-800 leading-snug mb-2 group-hover:text-rose-600 transition-colors line-clamp-2">
          {post.title}
        </h3>
        <p className="text-xs text-stone-400 line-clamp-2 mb-3">{post.excerpt}</p>
        <div className="flex items-center justify-between text-xs text-stone-400 pt-3 border-t border-stone-100">
          <span className="flex items-center gap-1 whitespace-nowrap">
            <i className="ri-user-line" />
            {post.author.split(' ')[0]}
          </span>
          <span className="whitespace-nowrap">{formatPublishDate(publishDate)}</span>
        </div>
      </div>
    </Link>
  );
}
